import { z } from 'zod';

import { getAvatarUrl } from './getAvatarUrl';

import type { Expert } from '@/services/getAlgoliaExperts';
import { CancerFocus, cancerFocusSchema } from '@/services/getCancerTypesV2';
import { UserV2 } from '@/services/getUserV2';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import { bioSectionSchema, mapToBioSections } from '@/util/mapBioSections';
import { Sentry } from '@/util/sentry';

export const expertProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  lastName: z.string().optional(),
  title: z.string().optional(),
  institution: z.string().optional(),
  bioSections: z.array(bioSectionSchema).optional(),
  focusArea: z.string().optional(),
  cancerFocuses: z.array(cancerFocusSchema).optional(),
  cancerFocusesDisplay: z.string().optional(),
  location: z.string().optional(),
  // TODO - data - avatarSrc url
  avatarSrc: z.string().optional(),
  initials: z.string().optional(),
  nameWithoutDoctor: z.string().optional(),
  biography: z.string().optional(),
  cognitoId: z.string().optional(),
  speciality: z.string().optional(),
  description: z.string().optional(),
  responseTime: z.number().optional(),
  isTest: z.boolean().optional(),
});

export type ExpertProfile = z.infer<typeof expertProfileSchema>;

/**
 * Maps Algolia expert data to ExpertProfile format for directory display.
 *
 * Uses ID-based lookup for cancer focus areas instead of using Algolia FocusArea field,
 * since FocusArea now contains only short labels (for display) and CancerTypeLabel contains
 * full labels (for searching). Focus area display prioritizes shortLabel over label when available.
 */
export const mapExpertToProfile = (
  expert: Expert,
  cancerFocuses: CancerFocus[],
  index: number = 0
): ExpertProfile => {
  const {
    objectID,
    Name,
    FocusArea,
    Location,
    Avatar,
    Position,
    Professional,
    WorkHistory,
    Education,
    Disclosures,
    Institution,
    identifiesAsDoctor,
    Biography,
    cognitoId,
    focusID,
    Speciality,
    responseTime,
  } = expert;

  const { firstName, lastName } = Name ?? { firstName: '', lastName: '' };

  const { city, state } = Location ?? { city: '', state: '' };

  const { name, initials, nameWithoutDoctor } = getProfileNameAndInitials({
    firstName,
    lastName,
    identifiesAsDoctor,
  });

  const expertCancerFocuses =
    focusID
      ?.map((focusId) => {
        return cancerFocuses.find((cancerFocus) => cancerFocus.id === focusId);
      })
      .filter(Boolean) || [];

  const focusArea = expertCancerFocuses
    .map((focus) => focus?.shortLabel || focus?.label || '')
    .filter(Boolean)
    .join(', ');

  const location = [city, state].filter(Boolean).join(', ');

  const mappedExpert = {
    id: objectID || `expert-${index}`,
    name,
    lastName,
    title: Position ?? undefined,
    institution: Institution ?? undefined,
    bioSections: mapToBioSections({
      about: Biography,
      workHistory: WorkHistory ?? [],
      education: Education ?? [],
      disclosures: Disclosures ?? [],
    }),
    focusArea,
    cancerFocuses: expertCancerFocuses,
    location: location || undefined,
    avatarSrc: getAvatarUrl(Avatar as UserV2['avatar']),
    initials,
    nameWithoutDoctor,
    biography: Biography ?? undefined,
    cognitoId,
    speciality: Speciality ?? undefined,
    responseTime,
    isTest: Name?.firstName === 'Test',
  };

  const result = expertProfileSchema.safeParse(mappedExpert);

  if (!result.success) {
    const error = result.error.flatten();

    Sentry.addBreadcrumb({
      category: 'mapExpertToProfile:safeParse:error',
      message: result.error.message,
      level: 'error',
      data: {
        data: mappedExpert,
        error,
      },
    });

    return mappedExpert as ExpertProfile;
  }

  return result.data;
};
